import { useCallback } from 'react';

import { company } from '~/company/Company';
import { ModalType } from '~/components/Modal/interface';
import { useModal } from '~/hooks/useModal';
import { useMounted } from '~/hooks/useMounted';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { checkoutStore, membershipStore, userStore } from '~/stores';

import { clubOffer, reservesClubEntry } from '../../domain/club';
import {
  CLUB_AUTH_NEXT_PARAM,
  CLUB_PLAN_PARAM,
  type ClubDeliveryOffer,
  type ClubEntrySource,
  cartZoneMismatch,
  clubDeliveryOffer,
  isClubMember,
  type MembershipEntryPayload,
} from '../../domain/clubEntry';
import {
  clubUpgradePlan,
  type MembershipPlanChangeSource,
} from '../../domain/manage';
import type { MembershipPlan } from '../../domain/types';
import { isCartRecalculating, useCartMembership } from '../useCartMembership';
import { readClubEntryHint } from './clubEntryHint';

/** Reports `Membership: Entry Viewed`; pass to `useViewedOnce`. */
export const reportEntryViewed = (payload: MembershipEntryPayload): void => {
  analyticsService.track(AnalyticsEvent.MEMBERSHIP_ENTRY_VIEWED, payload);
};

/**
 * The plan offered to a guest or a signed-in non-member of a company with the
 * club; `null` before mount, for a member, or while `/me` (signed in) and the
 * plans load. Call from an observer component.
 */
export const useClubOffer = (): MembershipPlan | null => {
  const mounted = useMounted();
  if (!mounted || !company.isMembershipsEnabled) {
    return null;
  }
  return clubOffer(membershipStore.view);
};

/**
 * An entry point holds its space: the offer of a guest or a signed-in
 * customer of a company with the club is still resolving (`/me`, the ETA or the zone's
 * plans have not arrived) and the entry points showed for this customer last
 * time, or nothing is known yet (`reservesClubEntry`). `false` before mount.
 * Call from an observer component.
 */
export const useReservesClubEntry = (): boolean => {
  const mounted = useMounted();
  return (
    mounted &&
    company.isMembershipsEnabled &&
    reservesClubEntry(
      membershipStore.view.kind === 'loading',
      readClubEntryHint(userStore.personalData.id),
    )
  );
};

/**
 * A member whose benefits apply now; `false` before mount and until `/me` is
 * loaded, so the header and the menu never flash. Call from an observer component.
 */
export const useIsClubMember = (): boolean => {
  const mounted = useMounted();
  return (
    mounted &&
    company.isMembershipsEnabled &&
    userStore.isAuthorized &&
    isClubMember(membershipStore.me)
  );
};

/**
 * The backend answered that the club is switched off (`MEMBERSHIPS_DISABLED`);
 * `false` before mount and until a club request has answered so, so the
 * server render and hydration agree. Call from an observer component.
 */
export const useIsClubDisabled = (): boolean => {
  const mounted = useMounted();
  return mounted && membershipStore.isDisabled;
};

/**
 * The club row of the cart and checkout, as calculate offers it; `null`
 * before mount, while calculate runs, until `/me` is loaded, while a purchase
 * is being activated, for a customer with a membership or when the plan would not make delivery free. Call from
 * an observer component.
 */
export const useClubDeliveryOffer = (): ClubDeliveryOffer | null => {
  const benefits = useCartMembership();
  if (
    benefits.kind !== 'upsell' ||
    !userStore.isAuthorized ||
    membershipStore.isDisabled ||
    isCartRecalculating()
  ) {
    return null;
  }
  return clubDeliveryOffer({
    benefits,
    me: membershipStore.me,
    activation: membershipStore.activation,
  });
};

/**
 * A member whose plan does not cover the current courier zone: the cart and
 * checkout say so instead of member benefits. `false` before mount, while
 * the zone is unchecked and for click & collect. Call from an observer component.
 */
export const useCartZoneMismatch = (): boolean => {
  const mounted = useMounted();
  return (
    mounted &&
    company.isMembershipsEnabled &&
    userStore.isAuthorized &&
    cartZoneMismatch(membershipStore.view, checkoutStore.isPickupSelected)
  );
};

/**
 * Opens Join on the current page, on `planId` when given, and reports
 * `Membership: Entry Clicked`; a guest signs in first and gets Join right after.
 */
export const useOpenClubJoin = (): ((
  source: ClubEntrySource,
  planCode: string | null,
  planId?: string | null,
) => void) => {
  const { openModal } = useModal();

  return useCallback(
    (
      source: ClubEntrySource,
      planCode: string | null,
      planId?: string | null,
    ) => {
      analyticsService.track(AnalyticsEvent.MEMBERSHIP_ENTRY_CLICKED, {
        source,
        planCode,
      });
      const plan: Record<string, string> = planId
        ? { [CLUB_PLAN_PARAM]: planId }
        : {};
      if (!userStore.isAuthorized) {
        openModal(ModalType.Auth, source, {
          [CLUB_AUTH_NEXT_PARAM]: 'join',
          ...plan,
        });
        return;
      }
      openModal(ModalType.ClubJoin, undefined, plan);
    },
    [openModal],
  );
};

/**
 * The plan a member whose plan misses the current courier zone can upgrade to,
 * under the same conditions as `useCartZoneMismatch`; `null` otherwise, also
 * when the membership allows no switch or no plan of the zone is an upgrade.
 * Call from an observer component.
 */
export const useClubUpgradeOffer = (): MembershipPlan | null => {
  const mounted = useMounted();
  const { view } = membershipStore;
  if (
    !mounted ||
    !company.isMembershipsEnabled ||
    !userStore.isAuthorized ||
    !cartZoneMismatch(view, checkoutStore.isPickupSelected) ||
    view.kind !== 'member'
  ) {
    return null;
  }
  return clubUpgradePlan(
    view.membership,
    membershipStore.switchablePlans,
    membershipStore.zoneId,
  );
};

/**
 * Reports `Membership: Plan Change Clicked` and opens the plan switch on
 * `plan` for the current member; does nothing without a membership.
 */
export const useOpenClubUpgrade = (): ((
  plan: MembershipPlan,
  source: MembershipPlanChangeSource,
) => void) => {
  const { openModal } = useModal();

  return useCallback(
    (plan: MembershipPlan, source: MembershipPlanChangeSource) => {
      const { view } = membershipStore;
      if (view.kind !== 'member') {
        return;
      }
      analyticsService.track(AnalyticsEvent.MEMBERSHIP_PLAN_CHANGE_CLICKED, {
        planCode: view.membership.plan.code,
        toPlanCode: plan.code,
        direction: 'upgrade',
        source,
      });
      openModal(ModalType.MembershipPlanChange, undefined, {
        [CLUB_PLAN_PARAM]: plan.id,
        source,
      });
    },
    [openModal],
  );
};
